'use client';

import React, { useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Link from 'next/link';
import { ArrowLeft2 } from 'iconsax-react';
import { CategoryNode } from '@/types/global';
import { Exchange } from '@/types/exchanges/page';
import ExchangeListCard from '@/components/(blog)/post/post-single-exchange-list/ExchangeListCard';

/** Panels stacked in one grid cell; the inactive ones are invisible but still take space. */
const PANEL =
    'flex flex-col gap-2 [grid-area:1/1] !mt-0 data-[state=inactive]:!flex data-[state=inactive]:invisible data-[state=inactive]:pointer-events-none data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200';

export interface PostSingleExchangeListProps {
    typeExchange?: string;
    category?: CategoryNode[] | undefined | null;
    iranianExchanges?: Exchange[];
    foreignExchanges?: Exchange[];
    brokers?: Exchange[];
    props?: Exchange[];
    /** Every prop slug: an article whose category matches one is a prop cluster article */
    propSlugs?: string[];
    adsBroker?: Exchange[];
    propAd?: Exchange[];
    exchangeForeignAd?: Exchange[];
    exchangeIranianAd?: Exchange[];
}

const containsSlugInHierarchy = (category: CategoryNode, slug: string): boolean => {
    if (category.slug === slug) {
        return true;
    }
    if (category.parent && category.parent.node) {
        return containsSlugInHierarchy(category.parent.node, slug);
    }
    return false;
};

const checkCategoriesForSlug = (
    categories: CategoryNode[] | undefined | null,
    slug: string,
): boolean => {
    if (!categories) return false;
    return categories.some((category) => containsSlugInHierarchy(category, slug));
};

const PostSingleExchangeList: React.FC<PostSingleExchangeListProps> = ({
    typeExchange,
    category,
    foreignExchanges,
    iranianExchanges,
    brokers,
    props,
    propSlugs,
    adsBroker,
    propAd,
    exchangeForeignAd,
    exchangeIranianAd,
}) => {
    const defaultTab: string = useMemo(() => {
        if (typeExchange) {
            return typeExchange;
        } else if (
            (props?.length || propAd?.length) &&
            (checkCategoriesForSlug(category, 'prop-trading') ||
                category?.some((cat) => cat.slug && propSlugs?.includes(cat.slug)))
        ) {
            return 'props';
        } else if (
            checkCategoriesForSlug(category, 'broker') ||
            checkCategoriesForSlug(category, 'forex')
        ) {
            return 'brokers';
        }
        return 'exchanges';
    }, [typeExchange, category, props?.length, propAd?.length, propSlugs]);

    if (
        !Array.isArray(foreignExchanges) ||
        !Array.isArray(iranianExchanges) ||
        !Array.isArray(brokers)
    ) {
        return null;
    }
    const MAX_ITEMS = 2;
    // Brokers and props show 4 rows each; ads come first and fill from the top
    const MAX_LIST_ITEMS = 4;

    const limitedAdsBroker = adsBroker?.slice(0, MAX_LIST_ITEMS);
    const limitedPropAd = propAd?.slice(0, MAX_LIST_ITEMS) || [];
    const propsWithoutExcludeItem = (props || [])
        .filter((prop) => !limitedPropAd.some((ad) => ad.databaseId === prop.databaseId))
        .slice(0, Math.max(0, MAX_LIST_ITEMS - limitedPropAd.length));
    const hasProps = limitedPropAd.length + propsWithoutExcludeItem.length > 0;
    const limitedExchangeForeignAd = exchangeForeignAd?.slice(0, MAX_ITEMS);
    const limitedExchangeIranianAd = exchangeIranianAd?.slice(0, MAX_ITEMS);

    const brokersWithoutExcludeItem = brokers
        ?.filter(
            (broker) =>
                !limitedAdsBroker?.some((exclude) => exclude.databaseId === broker.databaseId),
        )
        .slice(0, Math.max(0, MAX_LIST_ITEMS - (limitedAdsBroker?.length || 0)));

    const foreignWithoutExcludeItem = foreignExchanges
        ?.filter(
            (exchange) =>
                !limitedExchangeForeignAd?.some(
                    (exclude) => exclude.databaseId === exchange.databaseId,
                ),
        )
        .slice(0, Math.max(0, MAX_ITEMS - (limitedExchangeForeignAd?.length || 0)));

    const iranianWithoutExcludeItem = iranianExchanges
        ?.filter(
            (exchange) =>
                !limitedExchangeIranianAd?.some(
                    (exclude) => exclude.databaseId === exchange.databaseId,
                ),
        )
        .slice(0, Math.max(0, MAX_ITEMS - (limitedExchangeIranianAd?.length || 0)));

    // Rendered on the server (it used to wait for the browser: the section popped in late and
    // its links to the list pages were missing from the HTML crawlers read).
    return (
        <section className={'relative z-0 max-lg:mb-5 mt-5 mb-10'}>
            <Tabs defaultValue={defaultTab} dir={'rtl'}>
                <TabsList
                    className={
                        'mx-auto flex justify-between gap-2 p-0.5 rounded-lg border border-white/5 dark:bg-white/10 w-full mb-5 bg-bg h-auto'
                    }
                >
                    <TabsTrigger
                        value='exchanges'
                        className={
                            'TabsTrigger w-full flex-1 rounded-lg px-2 sm:px-6 py-2 font-light dark:data-[state=active]:bg-white dark:data-[state=active]:text-primary'
                        }
                    >
                        بهترین صرافی‌ها
                    </TabsTrigger>
                    <TabsTrigger
                        value='brokers'
                        className={
                            'TabsTrigger w-full flex-1 rounded-lg px-2 sm:px-6 py-2 font-light dark:data-[state=active]:bg-white dark:data-[state=active]:text-primary'
                        }
                    >
                        بهترین بروکرها
                    </TabsTrigger>
                    {hasProps && (
                        <TabsTrigger
                            value='props'
                            className={
                                'TabsTrigger w-full flex-1 rounded-lg px-2 sm:px-6 py-2 font-light dark:data-[state=active]:bg-white dark:data-[state=active]:text-primary'
                            }
                        >
                            بهترین پراپ
                        </TabsTrigger>
                    )}
                </TabsList>
                {/* All panels share one grid cell and stay mounted; the hidden ones keep their
                    space (invisible), so the box is always as tall as the tallest tab and switching
                    tabs never moves the page. */}
                <div className='grid'>
                    <TabsContent forceMount value='exchanges' className={PANEL}>
                        <div className={'inline-flex justify-start items-center gap-3'}>
                            <Link
                                prefetch={false}
                                href={'/crypto-exchange/'}
                                className={'inline-flex items-center gap-3 text-progress'}
                            >
                                <p className={'mb-1 text-lg font-bold'}>
                                    بهترین صرافی ارز دیجیتال خارجی
                                </p>

                                <ArrowLeft2
                                    className={
                                        'stroke-progress transition-all duration-300 group-hover:-translate-x-2 font-bold'
                                    }
                                    size={20}
                                />
                            </Link>
                        </div>
                        <ul className={'space-y-2'}>
                            {limitedExchangeForeignAd?.map(
                                (item: Exchange | undefined, index: number) => (
                                    <li key={item?.databaseId || `ad-foreign-${index}`}>
                                        <ExchangeListCard
                                            ads={true}
                                            childItem={item}
                                            index={index}
                                            rating={
                                                item?.exchangeFields?.generalInfo?.brokerRating
                                                    ?.finalRating || 0
                                            }
                                        />
                                    </li>
                                ),
                            )}
                            {foreignWithoutExcludeItem?.map((item, index) => (
                                <li key={item.databaseId || index}>
                                    <ExchangeListCard
                                        childItem={item}
                                        index={index}
                                        rating={
                                            item?.exchangeFields?.generalInfo?.brokerRating
                                                ?.finalRating || 0
                                        }
                                    />
                                </li>
                            ))}
                        </ul>
                        <div className={'inline-flex justify-start items-center gap-3 mt-5 '}>
                            <Link
                                prefetch={false}
                                className={'inline-flex items-center gap-3 text-progress'}
                                href={'/best-iranian-crypto-exchanges/'}
                            >
                                <p className={'mb-1 text-lg font-bold'}>
                                    بهترین صرافی ارز دیجیتال ایرانی
                                </p>

                                <ArrowLeft2
                                    className={
                                        'stroke-progress transition-all duration-300 group-hover:-translate-x-2'
                                    }
                                    size={20}
                                />
                            </Link>
                        </div>
                        <ul className={'space-y-2'}>
                            {limitedExchangeIranianAd?.map(
                                (item: Exchange | undefined, index: number) => (
                                    <li key={item?.databaseId || `ad-iranian-${index}`}>
                                        <ExchangeListCard
                                            ads={true}
                                            childItem={item}
                                            index={index}
                                            rating={
                                                item?.exchangeFields?.generalInfo?.brokerRating
                                                    ?.finalRating || 0
                                            }
                                        />
                                    </li>
                                ),
                            )}
                            {iranianWithoutExcludeItem?.map((item, index) => (
                                <li key={item.databaseId || index}>
                                    <ExchangeListCard
                                        childItem={item}
                                        index={index}
                                        rating={
                                            item?.exchangeFields?.generalInfo?.brokerRating
                                                ?.finalRating || 0
                                        }
                                    />
                                </li>
                            ))}
                        </ul>
                    </TabsContent>
                    <TabsContent forceMount value='brokers' className={PANEL}>
                        <div className={'inline-flex justify-start items-center gap-3'}>
                            <Link
                                prefetch={false}
                                className={'inline-flex items-center gap-3 text-progress'}
                                href={'/best-broker/'}
                            >
                                <p className={'mb-1 text-lg font-bold'}>بهترین بروکرها</p>
                                <ArrowLeft2
                                    className={
                                        'stroke-progress transition-all duration-300 group-hover:-translate-x-2'
                                    }
                                    size={20}
                                />
                            </Link>
                        </div>
                        <ul className={'space-y-2'}>
                            {limitedAdsBroker?.map((item: Exchange | undefined, index: number) => (
                                <li key={item?.databaseId || index}>
                                    <ExchangeListCard
                                        ads={true}
                                        childItem={item}
                                        index={index}
                                        rating={
                                            item?.brokerFields?.generalInfo?.brokerRating
                                                ?.finalRating || 0
                                        }
                                    />
                                </li>
                            ))}
                            {brokersWithoutExcludeItem?.map((item, index) => (
                                <li key={item.databaseId || index}>
                                    <ExchangeListCard
                                        childItem={item}
                                        index={index}
                                        rating={
                                            item?.brokerFields?.generalInfo?.brokerRating
                                                ?.finalRating || 0
                                        }
                                    />
                                </li>
                            ))}
                        </ul>
                    </TabsContent>
                    {hasProps && (
                        <TabsContent forceMount value='props' className={PANEL}>
                            <div className={'inline-flex justify-start items-center gap-3'}>
                                <Link
                                    prefetch={false}
                                    className={'inline-flex items-center gap-3 text-progress'}
                                    href={'/prop/'}
                                >
                                    <p className={'mb-1 text-lg font-bold'}>بهترین پراپ</p>
                                    <ArrowLeft2
                                        className={
                                            'stroke-progress transition-all duration-300 group-hover:-translate-x-2'
                                        }
                                        size={20}
                                    />
                                </Link>
                            </div>
                            <ul className={'space-y-2'}>
                                {limitedPropAd.map((item, index) => (
                                    <li key={item?.databaseId || `ad-prop-${index}`}>
                                        <ExchangeListCard
                                            ads={true}
                                            childItem={item}
                                            index={index}
                                            rating={
                                                item?.exchangeFields?.generalInfo?.brokerRating
                                                    ?.finalRating || 0
                                            }
                                        />
                                    </li>
                                ))}
                                {propsWithoutExcludeItem.map((item, index) => (
                                    <li key={item.databaseId || index}>
                                        <ExchangeListCard
                                            childItem={item}
                                            index={index}
                                            rating={
                                                item?.exchangeFields?.generalInfo?.brokerRating
                                                    ?.finalRating || 0
                                            }
                                        />
                                    </li>
                                ))}
                            </ul>
                        </TabsContent>
                    )}
                </div>
            </Tabs>
        </section>
    );
};

export default PostSingleExchangeList;
